import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  GalleryCounter,
  GalleryCanvas,
  GalleryDialog,
  GalleryImage,
  GalleryViewport,
  IconButton,
  NavigationButton,
  Toolbar,
} from "./ImageGallery.styles";

export interface ImageGalleryItem {
  src: string;
  alt?: string;
}

export interface ImageGalleryControls {
  openGallery: (index?: number) => void;
}

interface ImageGalleryProps {
  images: Array<ImageGalleryItem | string>;
  initialIndex?: number;
  children?: (controls: ImageGalleryControls) => React.ReactNode;
  ariaLabel?: string;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.25;

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  initialIndex = 0,
  children,
  ariaLabel = "Просмотр изображений",
}) => {
  const normalizedImages = useMemo<ImageGalleryItem[]>(
    () =>
      images.map((image, index) =>
        typeof image === "string"
          ? { src: image, alt: `Изображение ${index + 1}` }
          : image
      ),
    [images]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const dialogRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const zoomCenterRef = useRef({ x: 0.5, y: 0.5 });
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const normalizeIndex = useCallback(
    (index: number) => {
      if (!normalizedImages.length) {
        return 0;
      }

      return (
        (index % normalizedImages.length) + normalizedImages.length
      ) % normalizedImages.length;
    },
    [normalizedImages.length]
  );

  const openGallery = useCallback(
    (index = initialIndex) => {
      if (!normalizedImages.length) {
        return;
      }

      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      setActiveIndex(normalizeIndex(index));
      setScale(1);
      setIsOpen(true);
    },
    [initialIndex, normalizeIndex, normalizedImages.length]
  );

  const closeGallery = useCallback(() => {
    setIsOpen(false);
    setScale(1);
  }, []);

  const changeImage = useCallback(
    (direction: 1 | -1) => {
      if (normalizedImages.length < 2) {
        return;
      }

      setActiveIndex((currentIndex) =>
        normalizeIndex(currentIndex + direction)
      );
      setScale(1);
    },
    [normalizeIndex, normalizedImages.length]
  );

  const changeScale = useCallback((step: number) => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (viewport && canvas) {
      zoomCenterRef.current = {
        x: (viewport.scrollLeft + viewport.clientWidth / 2) / canvas.offsetWidth,
        y: (viewport.scrollTop + viewport.clientHeight / 2) / canvas.offsetHeight,
      };
    }

    setScale((currentScale) =>
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, currentScale + step))
    );
  }, []);

  const zoomIn = useCallback(() => changeScale(SCALE_STEP), [changeScale]);
  const zoomOut = useCallback(() => changeScale(-SCALE_STEP), [changeScale]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!isOpen || !viewport || !canvas) {
      return;
    }

    // Keep the same point under the viewport center as the canvas grows.
    viewport.scrollLeft =
      zoomCenterRef.current.x * canvas.offsetWidth - viewport.clientWidth / 2;
    viewport.scrollTop =
      zoomCenterRef.current.y * canvas.offsetHeight - viewport.clientHeight / 2;
  }, [isOpen, activeIndex, scale]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeGallery();
      } else if (event.key === "ArrowLeft") {
        changeImage(-1);
      } else if (event.key === "ArrowRight") {
        changeImage(1);
      } else if (event.key === "+" || event.key === "=") {
        zoomIn();
      } else if (event.key === "-") {
        zoomOut();
      } else if (event.key === "Tab") {
        const focusableElements = Array.from(
          dialogRef.current?.querySelectorAll<HTMLButtonElement>(
            "button:not(:disabled)"
          ) || []
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement.current?.focus();
    };
  }, [changeImage, closeGallery, isOpen, zoomIn, zoomOut]);

  useEffect(() => {
    if (isOpen && !normalizedImages.length) {
      closeGallery();
      return;
    }

    if (activeIndex >= normalizedImages.length) {
      setActiveIndex(0);
      setScale(1);
    }
  }, [activeIndex, closeGallery, isOpen, normalizedImages.length]);

  const currentImage = normalizedImages[activeIndex];
  const hasNavigation = normalizedImages.length > 1;

  return (
    <>
      {children?.({ openGallery })}

      {isOpen &&
        currentImage &&
        createPortal(
          <GalleryDialog
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeGallery();
              }
            }}
          >
            <Toolbar>
              <IconButton
                type="button"
                onClick={zoomOut}
                disabled={scale <= MIN_SCALE}
                aria-label="Уменьшить изображение"
                title="Уменьшить"
              >
                <i className="pi pi-search-minus" aria-hidden="true" />
              </IconButton>
              <IconButton
                type="button"
                onClick={zoomIn}
                disabled={scale >= MAX_SCALE}
                aria-label="Увеличить изображение"
                title="Увеличить"
              >
                <i className="pi pi-search-plus" aria-hidden="true" />
              </IconButton>
              <IconButton
                ref={closeButtonRef}
                type="button"
                onClick={closeGallery}
                aria-label="Закрыть галерею"
                title="Закрыть"
              >
                <i className="pi pi-times" aria-hidden="true" />
              </IconButton>
            </Toolbar>

            {hasNavigation && (
              <NavigationButton
                type="button"
                $side="left"
                onClick={() => changeImage(-1)}
                aria-label="Предыдущее изображение"
              >
                <i className="pi pi-chevron-left" aria-hidden="true" />
              </NavigationButton>
            )}

            <GalleryViewport
              ref={viewportRef}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <GalleryCanvas ref={canvasRef} $scale={scale}>
                <GalleryImage
                  key={currentImage.src}
                  src={currentImage.src}
                  alt={currentImage.alt || `Изображение ${activeIndex + 1}`}
                  $scale={scale}
                  draggable={false}
                />
              </GalleryCanvas>
            </GalleryViewport>

            {hasNavigation && (
              <NavigationButton
                type="button"
                $side="right"
                onClick={() => changeImage(1)}
                aria-label="Следующее изображение"
              >
                <i className="pi pi-chevron-right" aria-hidden="true" />
              </NavigationButton>
            )}

            {hasNavigation && (
              <GalleryCounter aria-live="polite">
                {activeIndex + 1} / {normalizedImages.length}
              </GalleryCounter>
            )}
          </GalleryDialog>,
          document.body
        )}
    </>
  );
};

export default ImageGallery;
